import React, { useContext, useCallback, useEffect, useState } from 'react';
import StudyHubBackNav from '../navigation/StudyHubBackNav';
import Navbar from '../Items/Navbar';
import AuthContext from '../shared/AuthContext';
import LearningFlashcard from '../learning/LearningFlashcard';
import LearningLiveBar from '../learning/LearningLiveBar';
import { getDailyReviewGoal } from '../../utils/learningPreferences';
import { normalizeLearningDeck } from '../../utils/normalizeLearningQuestion';
import useLiveLearningStats from '../../hooks/useLiveLearningStats';
import { showGamificationToast } from '../gamification/GamificationToast';
import { invalidateRealtime } from '../../realtime/realtimeBus';
import GamificationQuickBar from '../gamification/GamificationQuickBar';
import LearningGuide from '../learning/LearningGuide';
import NavShortcutGrid, { NavShortcut } from '../navigation/NavShortcutGrid';
import '../navigation/appNav.css';
import '../learning/learning-page-ui.css';
import '../learning/LearningFlashcard.css';
import '../learning/LearningLiveBar.css';

const ReviewPage = () => {
  const { apiClient, user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState('');
  const [reviewedSession, setReviewedSession] = useState(0);
  const dailyGoal = getDailyReviewGoal();
  const {
    dueCount: liveDueCount,
    savedCount,
    reviewedToday: liveReviewedToday,
    updatedAt,
    refreshing,
    refresh: refreshLiveStats,
  } = useLiveLearningStats();
  const userProfile = user || JSON.parse(localStorage.getItem('userProfile') || 'null');
  const current = items[0];

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/api/learning/review/due', { timeout: 30000 });
      const due = normalizeLearningDeck(response.data);
      setItems(due);
      setRevealed(false);
      if (!silent) {
        setMessage('');
      }
      refreshLiveStats(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load review queue.');
      if (!silent) setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiClient, refreshLiveStats]);

  useEffect(() => {
    load(false);
  }, [load]);

  const grade = async (value) => {
    if (!current) return;
    setGrading(value);
    const gradedId = current.id;
    try {
      const res = await apiClient.post(`/api/learning/review/${gradedId}`, { grade: value, context: 'review' });
      showGamificationToast(res.data?.gamification);
      invalidateRealtime('review-grade');
      setReviewedSession((n) => n + 1);
      setMessage(`Graded ${value.toLowerCase()}.`);
      setRevealed(false);
      setItems((prev) => prev.filter((q) => q.id !== gradedId));
      load(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setGrading('');
    }
  };

  const showSkeleton = loading && items.length === 0;

  return (
    <div className="learning-page">
      <Navbar userProfile={userProfile} loading={loading && items.length === 0} />
      <main className="learning-page-shell">
        <StudyHubBackNav />
        <h1 className="learning-page-title">Review</h1>
        <GamificationQuickBar />
        <LearningGuide focus="review" />
        <LearningLiveBar
          dueCount={items.length || liveDueCount}
          savedCount={savedCount}
          reviewedToday={Math.max(liveReviewedToday, reviewedSession)}
          dailyGoal={dailyGoal}
          updatedAt={updatedAt}
          refreshing={refreshing || loading}
          onRefresh={() => load(false)}
        />
        <p className="learning-page-lead">
          {items.length === 0 && !loading
            ? 'Nothing is due right now. Save questions and practice them — due items appear here when their interval elapses.'
            : `${items.length} question${items.length === 1 ? '' : 's'} due now.`}
          {' '}
          Session progress: {reviewedSession} graded
          {dailyGoal ? ` · goal ${dailyGoal}/day` : ''}.
        </p>
        {error && <p role="alert" className="learning-alert">{error}</p>}
        {message && !error && <p className="learning-status">{message}</p>}
        {showSkeleton && (
          <div className="learning-card-skeleton" aria-busy="true" aria-label="Loading due questions">
            <p />
            <p />
            <p />
          </div>
        )}
        {!showSkeleton && !current && !loading && (
          <div className="learning-empty">
            <p>Nothing due right now. Practice saved questions or add more from the library.</p>
            <NavShortcutGrid>
              <NavShortcut to="/practice" label="Practice" description="Build your memory" variant="primary" />
              <NavShortcut to="/library" label="Library" description="Save new questions" />
              <button type="button" className="nav-shortcut nav-shortcut--secondary" onClick={() => load(false)} style={{ cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                <span className="nav-shortcut-label">Refresh queue</span>
                <span className="nav-shortcut-desc">Check for newly due items</span>
              </button>
            </NavShortcutGrid>
          </div>
        )}
        {!showSkeleton && current && (
          <LearningFlashcard
            item={current}
            revealed={revealed}
            onReveal={() => setRevealed(true)}
            onGrade={grade}
            grading={grading}
            sessionLabel={`Due · ${items.length} remaining in queue`}
          />
        )}
      </main>
    </div>
  );
};

export default ReviewPage;
